import { useAuth } from "@clerk/react";
import { useState, type ReactNode } from "react";
import { Navigate } from "react-router-dom";

/** Where a signed-in visitor to `/readiness` belongs: their own Readiness
 *  page in the app, not the anonymous pre-signup quiz. */
export const SIGNED_IN_READINESS_HREF = "/dashboard?view=readiness";

/**
 * `/readiness` serves the anonymous readiness quiz (SILL-398) — kept, but
 * unlinked, as a possible future onboarding avenue (operator 2026-10-08).
 * A visitor who is ALREADY signed in when they arrive is redirected to the
 * in-app Readiness page instead.
 *
 * The decision is taken ONCE, on the first auth-loaded render, and frozen:
 * the quiz signs the visitor in mid-page (email OTP) and must finish
 * stitching the anon session before it navigates away itself, so a live
 * `isSignedIn` check would yank the page out from under that flow.
 */
export function AnonReadinessRoute({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  const [signedInOnArrival, setSignedInOnArrival] = useState<boolean | null>(
    null,
  );

  if (signedInOnArrival === null && isLoaded) {
    // Render-phase state set from a derived value (React's "adjust state
    // while rendering" pattern) — runs once, before anything paints.
    setSignedInOnArrival(Boolean(isSignedIn));
  }

  if (signedInOnArrival === null) return null;
  if (signedInOnArrival) return <Navigate replace to={SIGNED_IN_READINESS_HREF} />;
  return <>{children}</>;
}
